import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  query,
  getDocs,
  orderBy,
  startAfter,
  limit,
  QueryDocumentSnapshot,
  DocumentData,
} from "firebase/firestore";
import { db } from "../core/firebase";
import AppStoreLinks from "../components/AppStoreLinks";

type Post = {
  id: string;
  title?: string;
  author?: string;
  selftext?: string;
  created?: number;
  tags?: string[];
  source?: string;
  isHidden?: boolean;
  isApproved?: boolean;
};

const PAGE_SIZE = 12;

const formatTimeAgo = (createdSeconds?: number) => {
  if (!createdSeconds) return "";
  const ms = createdSeconds * 1000;
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(months / 12);
  return `${years}y ago`;
};

const stripHtml = (input?: string) => {
  if (!input) return "";
  return input.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
};

const HomeScreen = () => {
  const [stories, setStories] = useState<Post[]>([]);
  const [page, setPage] = useState(0);
  const [cursors, setCursors] = useState<
    Array<QueryDocumentSnapshot<DocumentData> | null>
  >([null]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchStories = async () => {
      setLoading(true);
      setError(null);
      try {
        const collectionRef = collection(db, "posts");
        const cursor = cursors[page];
        const q = cursor
          ? query(
              collectionRef,
              orderBy("created", "desc"),
              startAfter(cursor),
              limit(PAGE_SIZE)
            )
          : query(
              collectionRef,
              orderBy("created", "desc"),
              limit(PAGE_SIZE)
            );

        const snap = await getDocs(q);
        const docs = snap.docs;
        const result: Post[] = docs.map((d) => ({
          id: d.id,
          ...(d.data() as DocumentData),
        }));

        setStories(result);
        setHasMore(docs.length === PAGE_SIZE);

        const lastVisible = docs[docs.length - 1] ?? null;
        if (lastVisible && page === cursors.length - 1) {
          setCursors((c) => [...c, lastVisible]);
        }
      } catch (err: any) {
        setError(err?.message ?? "Failed to load stories.");
      } finally {
        setLoading(false);
      }
    };

    fetchStories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Hide moderated content from the public feed:
  //  - explicitly hidden stories
  //  - user submissions still pending approval (isApproved !== true)
  // Reddit-sourced posts have neither flag set — those still show.
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return stories.filter((s) => {
      if (s.isHidden) return false;
      if (s.source === "user" && s.isApproved !== true) return false;
      if (!term) return true;
      const haystack = [
        s.title,
        s.author,
        stripHtml(s.selftext),
        ...(s.tags ?? []),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [stories, search]);

  return (
    <div>
      {/* Hero */}
      <section className="text-center pt-4 pb-10 border-b border-ink-700/60">
        <p className="text-xs uppercase tracking-[0.4em] text-accent-400 mb-3">
          Tales from the dark
        </p>
        <h1 className="font-display text-5xl md:text-6xl text-white leading-tight">
          A Dark Place
        </h1>
        <p className="text-ink-300 mt-4 max-w-xl mx-auto">
          Read unsettling stories from across the web — and share your own.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <Link
            to="/submit"
            className="px-5 py-2.5 rounded-md bg-accent-500 hover:bg-accent-400 text-white text-sm tracking-wide"
          >
            Submit a story
          </Link>
          <a
            href="#feed"
            className="px-5 py-2.5 rounded-md border border-ink-600 hover:border-ink-400 text-ink-200 text-sm tracking-wide"
          >
            Start reading
          </a>
        </div>
      </section>

      {/* Search */}
      <section id="feed" className="mt-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
          <h2 className="font-display text-3xl text-white">Latest stories</h2>
          <div className="relative w-full md:w-72">
            <input
              type="text"
              placeholder="Search this page…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-ink-800 border border-ink-600 focus:border-accent-500 focus:outline-none rounded-md px-3 py-2 text-sm text-ink-100 placeholder:text-ink-400"
            />
          </div>
        </div>

        {error && (
          <div className="mb-6 text-sm text-red-300 bg-red-900/30 border border-red-800/50 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        {loading && stories.length === 0 ? (
          <div className="text-center py-16 text-ink-400">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="text-center py-16 text-ink-400">
            {search ? "No stories matched your search." : "No stories yet."}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {visible.map((story) => (
              <StoryCard key={story.id} story={story} />
            ))}
          </div>
        )}

        <div className="flex items-center justify-center space-x-4 mt-10">
          <button
            onClick={() => page > 0 && setPage((p) => p - 1)}
            disabled={page === 0 || loading}
            className="px-4 py-1.5 rounded-md bg-ink-700 hover:bg-ink-600 text-ink-100 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Newer
          </button>
          <span className="text-sm text-ink-400">Page {page + 1}</span>
          <button
            onClick={() => hasMore && setPage((p) => p + 1)}
            disabled={!hasMore || loading}
            className="px-4 py-1.5 rounded-md bg-ink-700 hover:bg-ink-600 text-ink-100 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Older
          </button>
        </div>
      </section>

      <AppStoreLinks variant="full" />
    </div>
  );
};

const StoryCard = ({ story }: { story: Post }) => {
  const preview = stripHtml(story.selftext).slice(0, 180);
  return (
    <Link
      to={`/story/${story.id}`}
      className="group flex flex-col bg-ink-800/70 hover:bg-ink-800 border border-ink-700 hover:border-accent-500/60 rounded-lg p-5 transition-colors shadow-glow/0 hover:shadow-glow"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-ink-400 mb-2">
        <span>{formatTimeAgo(story.created)}</span>
        {story.author && <span>· {story.author}</span>}
      </div>
      <h3 className="font-display text-2xl leading-tight text-white group-hover:text-white">
        {story.title || "(untitled)"}
      </h3>
      {preview && (
        <p className="text-ink-300 mt-2 text-sm leading-relaxed line-clamp-4">
          {preview}
          {preview.length === 180 ? "…" : ""}
        </p>
      )}
      {story.tags && story.tags.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {story.tags.slice(0, 4).map((t) => (
            <span
              key={t}
              className="text-[11px] uppercase tracking-wide text-ink-300 bg-ink-700/60 px-2 py-0.5 rounded"
            >
              {t}
            </span>
          ))}
        </div>
      )}
      <span className="text-xs text-accent-400 mt-4 group-hover:translate-x-0.5 transition-transform">
        Read story →
      </span>
    </Link>
  );
};

export default HomeScreen;
